import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Stack } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getDriverGuideUrlAPI } from "@/util/api/driverGuides";
import { Messages } from "@/util/Messages";
import type { AxiosError } from "axios";
import type { ErrorResponse } from "@/common/CommonConstants";

export const DriverGuideDownload = () => {
  const { id } = useParams<{ id: string }>();
  const hasFetched = useRef(false);
  const startTime = useRef(Date.now());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const downloadDriverGuide = async () => {
      const numId = Number(id);
      if (!id || Number.isNaN(numId) || numId <= 0) {
        console.error("Invalid driver guide ID:", id);
        setErrorMessage(`乗務員向けガイドの${Messages.DownloadFailed}`);
        return;
      }

      try {
        const res = await getDriverGuideUrlAPI(Number(id));
        const presignedUrl = res.data?.presignedUrl;
        if (typeof presignedUrl === "string" && presignedUrl.length > 0) {
          const minDisplayTime = 1000;
          const elapsed = Date.now() - startTime.current;
          const delay = Math.max(0, minDisplayTime - elapsed);
          setTimeout(() => {
            window.location.href = presignedUrl;
          }, delay);
        } else {
          console.error("Presigned URL not found in response:", res.data);
          setErrorMessage(`乗務員向けガイドの${Messages.DownloadFailed}`);
        }
      } catch (e) {
        const error = e as AxiosError<ErrorResponse>;
        console.error("Failed to download driver guide:", error?.response?.data);
        setErrorMessage(`乗務員向けガイドの${Messages.DownloadFailed}`);
      }
    };

    downloadDriverGuide();
  }, [id]);

  if (errorMessage) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Stack spacing={3} alignItems="center" sx={{ maxWidth: 600, px: 3 }}>
          <ErrorOutlineIcon sx={{ fontSize: 80, color: "error.main" }} />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            乗務員向けガイドを表示できません
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            {errorMessage}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Stack spacing={3} alignItems="center">
        <CircularProgress />
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          乗務員向けガイドのダウンロードを開始しています...
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          しばらくお待ちください。
        </Typography>
      </Stack>
    </Box>
  );
};
